/*
 * Copyright (C) 2005 - 2023. Cloud Software Group, Inc. All Rights Reserved. Confidential & Proprietary.
 * Licensed pursuant to commercial Cloud Software Group, Inc. End User License Agreement.
 */

import $ from 'jquery';
import 'perfect-scrollbar';
import React, { Component, RefObject } from 'react';
import CrosstabLevelsPart from './CrosstabLevelsPart';
import { IMultiAxisDataServiceDataLayoutMemberExpansion, } from '../../../../store/types/MultiAxisDatasetTypes';
import { CrosstabDataPart } from './CrosstabDataPart';
import { CrosstabHeaderPart } from './CrosstabHeaderPart';
import {
    AxisDescriptors,
    IColumnDescriptors,
    ICrosstabAdaptiveSizeLayoutManagerProps,
    ICrosstabVisualizationDynamicProperties,
    IInternalCrosstabHeaderRow,
    IRowDescriptors,
    OnScrollParameter
} from '../types/CrosstabVisualizationTypes';
import { IAxisItem, TGroupedMeasures } from '../../../../store/types/StateTypes';

const HEADER_SCROLL_THRESHOLD = 20;

function calculateScrollPosition(preparedDataArray: any[], positionOnNode: any, sizeResolver: (index: number) => number, offset: number) {
    let renderedOffset = 0,
        i = 0;
    while (preparedDataArray[i] && (preparedDataArray[i].index !== positionOnNode.nodeReference.index)) {
        renderedOffset += sizeResolver(preparedDataArray[i].index);
        i += 1;
    }

    return offset + renderedOffset - positionOnNode.viewPortOffset;
}

function calculateViewPortOffset(preparedDataArray: any[], sizeResolver: (index: number) => number, preparedDataOffset: number, offset: number, scrollOffset: number) {
    let renderedOffset = 0;
    for (let i = 0; i < preparedDataOffset; i += 1) {
        renderedOffset += sizeResolver(preparedDataArray[i].index);
    }
    return offset + renderedOffset - scrollOffset;
}

function readMeasuresInformationForDescriptors(items: (IAxisItem | TGroupedMeasures)[], values: IInternalCrosstabHeaderRow[]) {
    const groupedMeasures = items.filter((item) => item.kind === 'measures')[0] as TGroupedMeasures;
    if (groupedMeasures) {
        const defaultMeasure = groupedMeasures.measures[0];
        const measures = groupedMeasures.measures.reduce((mm: any, msr) => {
            const measure = msr as IAxisItem;
            const memo = mm;
            memo[measure.id] = measure;
            return memo;
        }, {});

        return values.map((value) => {
            const measureNode = value.nodes.find(((node) => (node ? node.isMeasure : false)));
            return measureNode && measures[measureNode.label] ? measures[measureNode.label] : defaultMeasure;
        });
    }

    return [];
}

export default class CrosstabAdaptiveSizeLayoutManager extends Component<ICrosstabAdaptiveSizeLayoutManagerProps> {
    crosstabRef: RefObject<HTMLDivElement>;

    levelsPartContainerRef: RefObject<HTMLDivElement>;

    rowsPartContainerRef: RefObject<HTMLDivElement>;

    columnsPartContainerRef: RefObject<HTMLDivElement>;

    dataPartContainerRef: RefObject<HTMLDivElement>;

    scrollInitialized: boolean;

    // TODO dispose of jQuery and switch to full React flow
    heightAdjusted: boolean;

    constructor(a: any, b: any) {
        super(a, b);

        this.crosstabRef = React.createRef<HTMLDivElement>();
        this.levelsPartContainerRef = React.createRef<HTMLDivElement>();
        this.rowsPartContainerRef = React.createRef<HTMLDivElement>();
        this.columnsPartContainerRef = React.createRef<HTMLDivElement>();
        this.dataPartContainerRef = React.createRef<HTMLDivElement>();

        this.scrollInitialized = false;
        this.heightAdjusted = false;

        this.onScroll = this.onScroll.bind(this);
        this.onRowHeaderScroll = this.onRowHeaderScroll.bind(this);
        this.scrollOnMouseWheel = this.scrollOnMouseWheel.bind(this);
        this.onLevelExpansionChange = this.onLevelExpansionChange.bind(this);
        this.onMemberExpansionChange = this.onMemberExpansionChange.bind(this);
    }

    componentDidMount() {
        this.postRenderOperations();
    }

    componentDidUpdate() {
        this.postRenderOperations();
    }

    componentWillUnmount() {
        this.turnOffScrollbars();
    }

    onScroll() {
        if (this.dataPartContainerRef.current) {
            const dataDom = this.dataPartContainerRef.current;
            const table = dataDom.getElementsByClassName('jr-mDatatableCrosstab')[0];

            const {
                scrollTop, scrollLeft, clientHeight: viewPortHeight, clientWidth: viewPortWidth
            } = dataDom;
            const { clientHeight: renderedHeight, clientWidth: renderedWidth } = table;

            const param = {
                scrollTop, scrollLeft, viewPortHeight, viewPortWidth, renderedHeight, renderedWidth
            };

            this.actualizeOnScroll(param);
            this.props.onScroll(param);
        }
    }

    onRowHeaderScroll() {
        if (this.levelsPartContainerRef.current && this.rowsPartContainerRef.current) {
            this.levelsPartContainerRef.current.scrollLeft = this.rowsPartContainerRef.current.scrollLeft;
        }
    }

    onLevelExpansionChange(level: any, expanded: boolean, isColumns: boolean) {
        if (isColumns) {
            if (this.columnsPartContainerRef.current && this.dataPartContainerRef.current) {
                this.columnsPartContainerRef.current.scrollLeft = 0;
                this.dataPartContainerRef.current.scrollLeft = 0;
            }
        } else if (this.rowsPartContainerRef.current && this.dataPartContainerRef.current) {
            this.rowsPartContainerRef.current.scrollTop = 0;
            this.dataPartContainerRef.current.scrollTop = 0;
        }

        this.props.onLevelExpansionChange(level, expanded);
    }

    onMemberExpansionChange(memberExpansion: IMultiAxisDataServiceDataLayoutMemberExpansion, isColumns: boolean, rowN: number, colN: number) {
        let dProps;
        if (!memberExpansion.expanded && this.dataPartContainerRef.current) {
            const {
                preparedData,
                dynamicProperties: {
                    minimalCellWidth, minimalCellHeight, bodyWidths, offsetTop, offsetLeft
                }
            } = this.props;
            let sizeResolver,
                viewPortOffset;
            if (isColumns) {
                // TODO reusable size resolver
                sizeResolver = (nodeIndex: number) => (bodyWidths[nodeIndex] || minimalCellWidth);
                viewPortOffset = calculateViewPortOffset(preparedData.columns, sizeResolver, rowN, offsetLeft, this.dataPartContainerRef.current.scrollLeft);
            } else {
                // TODO reusable size resolver
                sizeResolver = (nodeIndex: number) => (minimalCellHeight);
                viewPortOffset = calculateViewPortOffset(preparedData.rows, sizeResolver, rowN, offsetTop, this.dataPartContainerRef.current.scrollTop);
            }

            dProps = {
                [isColumns ? 'offsetLeft' : 'offsetTop']: -1,
                positionOnNode: {
                    [isColumns ? 'columns' : 'rows']: {
                        viewPortOffset,
                        nodeReference: preparedData[isColumns ? 'columns' : 'rows'][rowN]
                    },
                    [isColumns ? 'rows' : 'columns']: null
                }
            }
        } else {
            dProps = {
                positionOnNode: null
            }
        }

        this.props.onMemberExpansionChange(memberExpansion, isColumns, dProps);
    }

    scrollOnMouseWheel(event :any) {
        if (typeof event.deltaY === 'undefined') {
            return;
        }
        const crosstabBody = this.dataPartContainerRef.current;
        if (!crosstabBody) {
            return;
        }
        const verticalDelta = event.deltaY;
        crosstabBody.scrollTop += verticalDelta;
    }

    adjustToContent() {
        if (this.crosstabRef.current && this.dataPartContainerRef.current && this.columnsPartContainerRef.current && this.rowsPartContainerRef.current) {
            const {
                dataset,
                preparedData: {
                    knownHeight, knownWidth
                },
                staticProperties: {
                    properties
                },
                dynamicProperties: {
                    xPointX, xPointY, crosstabWidth, crosstabHeight
                }
            } = this.props;

            const $actualData = $(this.dataPartContainerRef.current).find('.jr-mDatatable');
            const actualDataWidth = $actualData.width() || crosstabWidth - xPointX;
            const actualDataHeight = $actualData.height() || crosstabHeight - xPointY;

            if (properties.takeAllAvailableWidth) {
                if (crosstabWidth - xPointX > actualDataWidth) {
                    this.crosstabRef.current.classList.add('jr-isStretched');
                }
            } else {
                $(this.dataPartContainerRef.current).width(Math.min(crosstabWidth - xPointX - 1, actualDataWidth));
                $(this.columnsPartContainerRef.current).width(Math.min(crosstabWidth - xPointX - 1, actualDataWidth));
            }

            if (crosstabHeight - xPointY > actualDataHeight) {
                $(this.dataPartContainerRef.current).height(actualDataHeight);
                $(this.rowsPartContainerRef.current).height(actualDataHeight);
                this.heightAdjusted = true;
            } else if (this.heightAdjusted) {
                $(this.rowsPartContainerRef.current).height(crosstabHeight - xPointY - 1);
                $(this.dataPartContainerRef.current).height(crosstabHeight - xPointY - 1);
                this.heightAdjusted = true;
            }

            if (this.dataPartContainerRef.current.scrollLeft + this.dataPartContainerRef.current.offsetWidth > knownWidth) {
                this.dataPartContainerRef.current.scrollLeft = this.columnsPartContainerRef.current.scrollLeft;
            }

            if (this.dataPartContainerRef.current.scrollTop + this.dataPartContainerRef.current.offsetHeight > knownHeight) {
                this.dataPartContainerRef.current.scrollTop = this.rowsPartContainerRef.current.scrollTop;
            }

            if (!dataset.summary.rows.items.length) {
                $(this.crosstabRef.current).find('.jr-mDatatableCrosstab-bodyContainer').addClass('jr-isEmpty');
            }
        }
    }

    unAdjustToContent() {
        if (this.crosstabRef.current) {
            this.crosstabRef.current.classList.remove('jr-isStretched');
            $(this.crosstabRef.current).find('.jr-mDatatableCrosstab-bodyContainer').removeClass('jr-isEmpty');
        }
    }

    turnOnScrollbars() {
        if (this.dataPartContainerRef.current && this.rowsPartContainerRef.current) {
            if (this.scrollInitialized) {
                // @ts-ignore
                $(this.dataPartContainerRef.current).perfectScrollbar('update');
                // @ts-ignore
                $(this.rowsPartContainerRef.current).perfectScrollbar('update');
                $(this.rowsPartContainerRef.current).find('.ps-scrollbar-y-rail').hide();
            } else {
                // @ts-ignore
                $(this.dataPartContainerRef.current).perfectScrollbar();
                this.dataPartContainerRef.current.addEventListener('scroll', this.onScroll);
                // @ts-ignore
                $(this.rowsPartContainerRef.current).perfectScrollbar();
                $(this.rowsPartContainerRef.current).find('.ps-scrollbar-y-rail').hide();
                this.rowsPartContainerRef.current.addEventListener('scroll', this.onRowHeaderScroll);
                this.rowsPartContainerRef.current.addEventListener('wheel', this.scrollOnMouseWheel);
                this.scrollInitialized = true;
            }

            // This is the fix for JS-62183 Crosstab: Chrome: Right click on data in an expanded roll will make the content scrolled up to top
            // @ts-ignore
            $(this.rowsPartContainerRef.current).off('mouseup', this.onScroll).on('mouseup', this.onScroll);
            // end fix
        }
    }

    turnOffScrollbars() {
        if (this.dataPartContainerRef.current && this.rowsPartContainerRef.current) {
            // @ts-ignore
            $(this.dataPartContainerRef.current).perfectScrollbar('destroy');
            this.dataPartContainerRef.current.removeEventListener('scroll', this.onScroll);

            // @ts-ignore
            $(this.rowsPartContainerRef.current).perfectScrollbar('destroy');
            this.rowsPartContainerRef.current.removeEventListener('wheel', this.scrollOnMouseWheel);

            this.scrollInitialized = false;
        }
    }

    positionParts(newDynamicProps: ICrosstabVisualizationDynamicProperties): ICrosstabVisualizationDynamicProperties {
        const res = newDynamicProps;
        if (this.crosstabRef.current && this.levelsPartContainerRef.current && this.rowsPartContainerRef.current && this.dataPartContainerRef.current) {
            const {
                current: {
                    offsetWidth: xPointX,
                    offsetHeight: xPointY
                }
            } = this.levelsPartContainerRef;

            const {
                current: {
                    clientWidth: crosstabWidth,
                    clientHeight: crosstabHeight
                }
            } = this.crosstabRef;

            const {
                dynamicProperties: oldDynamicProps
            } = this.props;

            let newXpointX = Math.max(xPointX, oldDynamicProps.headerWidths.reduce((memo: number, width: number) => (memo + width), oldDynamicProps.centralBorderWidth));
            if (newDynamicProps.headerWidths) {
                newXpointX = Math.max(xPointX, newDynamicProps.headerWidths.reduce((memo: number, width: number) => (memo + width), oldDynamicProps.centralBorderWidth));
            }

            const $actualData = $(this.dataPartContainerRef.current).find('.jr-mDatatable');
            const actualDataWidth = $actualData.width() || crosstabWidth;
            const availableRowHeaderWidth = Math.max(crosstabWidth / 2, crosstabWidth - actualDataWidth) - oldDynamicProps.centralBorderWidth;

            if (availableRowHeaderWidth < newXpointX && newXpointX - availableRowHeaderWidth > HEADER_SCROLL_THRESHOLD) {
                newXpointX = Math.floor(availableRowHeaderWidth);
            }

            if (oldDynamicProps.xPointX !== newXpointX) {
                res.xPointX = newXpointX;
            }

            if (oldDynamicProps.xPointY !== xPointY) {
                res.xPointY = xPointY;
            }

            if (oldDynamicProps.crosstabWidth !== crosstabWidth) {
                res.crosstabWidth = crosstabWidth;
            }

            if (oldDynamicProps.crosstabHeight !== crosstabHeight) {
                res.crosstabHeight = crosstabHeight;
            }
        }
        return res;
    }

    alignRowHeaders(dynamicProps: ICrosstabVisualizationDynamicProperties): ICrosstabVisualizationDynamicProperties {
        const res = dynamicProps;

        if (this.rowsPartContainerRef.current && this.levelsPartContainerRef.current && this.crosstabRef.current) {
            const bottomRow = this.rowsPartContainerRef.current.getElementsByClassName('jr-mDatatable-row');
            if (bottomRow.length) {
                const { headerWidths } = this.props.dynamicProperties;
                const top = this.levelsPartContainerRef.current.getElementsByClassName('jr-mDatatable-cellColumnlabel');
                const bottom = bottomRow[0].getElementsByClassName('jr-mDatatable-cell');

                let changed = false;

                if (top.length) {
                    for (let i = 0; i < top.length; i += 1) {
                        const width = Math.ceil(Math.max((top[i] as HTMLElement).getBoundingClientRect().width, (bottom[i] as HTMLElement).getBoundingClientRect().width));
                        if (headerWidths[i] !== width && headerWidths[i] + 1 !== width) {
                            headerWidths[i] = width;
                            changed = true;
                        }
                    }
                } else if (bottom.length) {
                    // in this case there is nothing in rows, apply extra styling to calculate its size properly
                    $(this.crosstabRef.current).find('.jr-mDatatableCrosstab-bodyContainer').addClass('jr-isEmpty');
                    const width = (bottom[0] as HTMLElement).offsetWidth;
                    if (headerWidths[0] !== width) {
                        headerWidths[0] = width;
                        changed = true;
                    }
                    // remove the extra styling at this stage, it will be added back at the last stage
                    $(this.crosstabRef.current).find('.jr-mDatatableCrosstab-bodyContainer').removeClass('jr-isEmpty');
                }

                if (changed) {
                    res.headerWidths = [...headerWidths];
                }
            }
        }

        return res;
    }

    alignColumnHeaders(dynamicProps: ICrosstabVisualizationDynamicProperties): ICrosstabVisualizationDynamicProperties {
        const res = dynamicProps;
        const oldDynamicProps = this.props.dynamicProperties;

        if (this.columnsPartContainerRef.current && this.dataPartContainerRef.current) {
            const bottomRow = this.dataPartContainerRef.current.getElementsByClassName('jr-mDatatable-row');

            if (bottomRow.length) {
                const top = this.columnsPartContainerRef.current.getElementsByClassName('jr-mDatatable-cellSpacer');
                const bottom = bottomRow[0].getElementsByClassName('jr-mDatatable-cell');

                const bodyWidths = oldDynamicProps.bodyWidths.slice();
                let changed = false;
                let newMinWidth = oldDynamicProps.minimalCellWidth;

                const indexes = this.props.preparedData.columns.map((column: any) => (column.index));

                for (let i = 0; i < top.length; i += 1) {
                    const width = Math.max(
                        (top[i] as HTMLElement).offsetWidth,
                        (bottom[i] as HTMLElement).offsetWidth,
                        oldDynamicProps.minimalCellWidth
                    );

                    newMinWidth = Math.min(newMinWidth, width);

                    if (bodyWidths[indexes[i]] !== width) {
                        bodyWidths[indexes[i]] = width;
                        changed = true;
                    }
                }

                if (changed) {
                    res.bodyWidths = bodyWidths;
                }

                if (newMinWidth !== oldDynamicProps.minimalCellWidth) {
                    res.minimalCellWidth = newMinWidth;
                }
            }
        }

        return dynamicProps;
    }

    updateScrollPositions(dProps: ICrosstabVisualizationDynamicProperties): ICrosstabVisualizationDynamicProperties {
        if (this.props.dynamicProperties.positionOnNode
            && this.columnsPartContainerRef.current
            && this.rowsPartContainerRef.current
            && this.dataPartContainerRef.current) {
            const {
                preparedData,
                dynamicProperties: {
                    minimalCellWidth, minimalCellHeight, bodyWidths, offsetTop, offsetLeft, positionOnNode
                }
            } = this.props;

            let scrollOffset,
                sizeResolver;
            if (positionOnNode.columns) {
                // TODO reusable size resolver
                sizeResolver = (nodeIndex: number) => (bodyWidths[nodeIndex] || minimalCellWidth);
                scrollOffset = calculateScrollPosition(preparedData.columns, positionOnNode.columns, sizeResolver, offsetLeft);

                this.columnsPartContainerRef.current.scrollLeft = scrollOffset;
                this.dataPartContainerRef.current.scrollLeft = scrollOffset;
            }

            if (positionOnNode.rows) {
                // TODO reusable size resolver
                sizeResolver = (nodeIndex: number) => (minimalCellHeight);
                scrollOffset = calculateScrollPosition(preparedData.rows, positionOnNode.rows, sizeResolver, offsetTop);

                this.rowsPartContainerRef.current.scrollTop = scrollOffset;
                this.dataPartContainerRef.current.scrollTop = scrollOffset;
            }
        }
        return dProps;
    }

    postRenderOperations() {
        this.unAdjustToContent();

        let dynamicProps = {} as ICrosstabVisualizationDynamicProperties;

        dynamicProps = this.alignRowHeaders(dynamicProps);
        dynamicProps = this.alignColumnHeaders(dynamicProps);
        dynamicProps = this.positionParts(dynamicProps);
        dynamicProps = this.updateScrollPositions(dynamicProps);

        if (Object.keys(dynamicProps).length) {
            this.props.handle.updateDynamicProperties(dynamicProps);
        } else {
            this.onScroll();
            this.adjustToContent()
            this.turnOnScrollbars();
        }

    }

    actualizeOnScroll(param: OnScrollParameter) {
        if (this.rowsPartContainerRef.current && this.columnsPartContainerRef.current) {
            const { scrollTop, scrollLeft } = param;

            this.rowsPartContainerRef.current.scrollTop = scrollTop;
            this.columnsPartContainerRef.current.scrollLeft = scrollLeft;
        }
    }

    makeDescriptors(): AxisDescriptors {
        const {
            preparedData,
            staticProperties: {
                levelsStructure
            },
            dynamicProperties: {
                bodyWidths, minimalCellWidth
            }
        } = this.props;

        // collect measures
        const { rows, columns } = levelsStructure;

        // TODO extend and review
        const columnDescriptors: IColumnDescriptors = {
            widths: preparedData.columns.map((column: IInternalCrosstabHeaderRow) => (bodyWidths[column.index] || minimalCellWidth)),
            columnMeasures: readMeasuresInformationForDescriptors(columns, preparedData.columns)
        };

        const rowDescriptors: IRowDescriptors = {
            rowMeasures: readMeasuresInformationForDescriptors(rows, preparedData.rows)
        };

        return { columnDescriptors, rowDescriptors };
    }

    render() {
        const {
            dataset,
            preparedData,
            staticProperties: {
                levelsStructure,
                properties
            },
            dynamicProperties: {
                xPointX, xPointY, crosstabWidth, crosstabHeight, headerWidths,
                bodyWidths, offsetTop, offsetLeft
            },
            linkOptions,
            hyperlinksCollector
        } = this.props;

        const showEmptyMessage = dataset.summary.empty
            || !dataset.summary.columns.items.concat(dataset.summary.rows.items).find((item: any) => (item.measuresLevel))

        return (
            <div ref={this.crosstabRef} className="jr-mDatatableCrosstabContainer jr">
                <div className="jr-mDatatableCrosstab-headerContainer jr">
                    <div
                        ref={this.levelsPartContainerRef}
                        className="jr-mDatatableCrosstab-header-rowlabelsContainer jr"
                        style={{ width: xPointX }}
                    >
                        <CrosstabLevelsPart
                            dataset={dataset}
                            levelsStructure={levelsStructure}
                            headerWidths={headerWidths}
                            onLevelExpansionChange={this.onLevelExpansionChange}
                        />
                    </div>
                    <div
                        ref={this.columnsPartContainerRef}
                        className="jr-mDatatableCrosstab-header-columnlabelsContainer jr"
                        style={{ left: xPointX, width: crosstabWidth - xPointX, height: xPointY }}
                    >
                        <CrosstabHeaderPart
                            columnsHeader
                            headerWidths={bodyWidths}
                            offset={offsetLeft}
                            nodes={preparedData.columns}
                            levelsStructure={levelsStructure}
                            sorting={dataset.summary.order}
                            properties={properties}
                            onMemberExpansionChange={this.onMemberExpansionChange}
                            linkOptions={linkOptions}
                            hyperlinksCollector={hyperlinksCollector}
                        />
                    </div>
                </div>
                <div className="jr-mDatatableCrosstab-bodyContainer jr">
                    <div
                        ref={this.rowsPartContainerRef}
                        className="jr-mDatatableCrosstab-body-rowlabelsContainer jr"
                        style={{ width: xPointX, top: xPointY, height: crosstabHeight - xPointY }}
                    >
                        <CrosstabHeaderPart
                            columnsHeader={false}
                            headerWidths={headerWidths}
                            offset={offsetTop}
                            nodes={preparedData.rows}
                            levelsStructure={levelsStructure}
                            sorting={dataset.summary.order}
                            properties={properties}
                            onMemberExpansionChange={this.onMemberExpansionChange}
                            linkOptions={linkOptions}
                            hyperlinksCollector={hyperlinksCollector}
                        />
                    </div>
                    <div
                        ref={this.dataPartContainerRef}
                        className="jr-mDatatableCrosstab-body-dataContainer jr"
                        style={{
                            left: xPointX,
                            top: xPointY,
                            width: crosstabWidth - xPointX,
                            height: crosstabHeight - xPointY
                        }}
                    >
                        <div className="jr-mDatatableCrosstab-background" style={{ width: preparedData.knownWidth, height: preparedData.knownHeight }} />

                        <CrosstabDataPart
                            data={preparedData.data}
                            empty={showEmptyMessage}
                            rowNodes={preparedData.rows}
                            columnNodes={preparedData.columns}
                            levelsStructure={levelsStructure}
                            offsetTop={offsetTop}
                            offsetLeft={offsetLeft}
                            descriptors={this.makeDescriptors()}
                            linkOptions={linkOptions}
                            hyperlinksCollector={hyperlinksCollector}
                        />
                    </div>
                </div>
            </div>
        )
    }
}
